import { useMemo, useEffect } from 'react';

function useInstanceOrUpdateChart(base, data) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ChartData = useMemo(() => base.column(data), [base]);
  useEffect(() => {
    const dataSet = ChartData.data().getDataSets()[0];
    for (let i = 0; i < data.getRowsCount(); i++) {
      dataSet.row(i, data.row(i));
    }
  }, [data, ChartData]);
  return ChartData;
}

const updateInformationTableFunc = () => (
  infoContainerTable,
  infoTableRef,
  chartInstance,
) => {
  if (infoContainerTable.current && infoTableRef.current) {
    const bounds = chartInstance.getPlotBounds();
    const tableWidth = infoTableRef.current.clientWidth;
    const columnWidth = bounds.width - tableWidth;
    //        Coluna 1  Coluna 2
    // Largura   colW      tabW
    // Ratio      2        x  \/
    // colW x = 2 tabW
    const tableRatio = (2.25 * tableWidth) / columnWidth;
    infoContainerTable.current.style.height = `${bounds.height}px`;
    infoContainerTable.current.style.width = `${bounds.width}px`;
    infoContainerTable.current.style.top = `${bounds.top}px`;
    infoContainerTable.current.style.left = `${bounds.left}px`;
    chartInstance.barGroupsPadding(0.25); // Makes the group have low padding
    // Makes the space between the charts to be 90% of the available space
    chartInstance.barsPadding(tableRatio);
  }
};

export const useChart1 = (
  chartRef,
  anychart,
  Impactos,
  E,
  containerRef,
  tableRef,
) => {
  const chartData = {
    title:
      'Produtividade em unidades de produção:' +
      (E('unidadeProducao') || '') +
      ' /ha',
    header: ['#', 'Recomendação Convencional', 'Recomendação SQM VITAS Brasil'],
    rows: [
      [
        'Produtividades ' + (E('unidadeProducao') || 'kg') + ' /ha',
        E('produtividadeHa'),
        Impactos.produtividade,
      ],
    ],
  };
  const updateInformationTable = useMemo(
    () => updateInformationTableFunc(),
    [],
  );

  const returnData = chartData.rows[0].slice(1).reverse();

  const dataSet = anychart.data.set(chartData.rows);

  const SqmVitasData = dataSet.mapAs({
    x: 0,
    value: 1,
  });

  const ConvencionalData = dataSet.mapAs({
    x: 0,
    value: 2,
  });

  // create column chart
  const chart = useMemo(() => anychart.column(), [anychart]);
  useEffect(() => {
    chartRef.current = chart;
  }, [chart, chartRef]);

  // set title chart
  chart.title(chartData.title);

  // turn on chart animation
  chart.animation(true);

  // create first series with mapped data
  const SqmVitas = useInstanceOrUpdateChart(chart, SqmVitasData, 'sqm');
  SqmVitas.name(chartData.header[1]);
  SqmVitas.normal().fill('#f26c31');
  SqmVitas.normal().stroke('#f26c31');

  SqmVitas.hovered().fill('#f26c31', 0.5);
  SqmVitas.hovered().stroke('#f26c31', 4);

  SqmVitas.selected().fill('#f26c31', 0.5);
  SqmVitas.selected().stroke('#f26c31', 4);

  // create second series with mapped data
  const Convencional = useInstanceOrUpdateChart(
    chart,
    ConvencionalData,
    'conv',
  );
  Convencional.name(chartData.header[2]);
  Convencional.normal().fill('#00b050');
  Convencional.normal().stroke('#00b050');

  Convencional.hovered().fill('#00b050', 0.5);
  Convencional.hovered().stroke('#00b050', 4);

  Convencional.selected().fill('#00b050', 0.5);
  Convencional.selected().stroke('#00b050', 4);

  chart
    .yAxis()
    .labels()
    .format(
      (e) =>
        `${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart
    .labels()
    .enabled(true)
    .position('center-top')
    .anchor('center-bottom')
    .format(
      (e) =>
        `${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart.hovered().labels(false);

  // turn on legend and tune it
  chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);

  // interactivity settings and tooltip position
  chart.interactivity().hoverMode('single');

  chart
    .tooltip()
    .positionMode('point')
    .position('center-top')
    .anchor('center-bottom')
    .offsetX(0)
    .offsetY(5)
    .titleFormat('{%X}')
    .format(
      (e) =>
        `${e.seriesName} : ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  // set container id for the chart
  chart.container('chart1');

  // initiate chart drawing
  chart.draw();

  useEffect(() => {
    chart.listen('chartDraw', () => {
      updateInformationTable(containerRef, tableRef, chart);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, updateInformationTableFunc]);

  return returnData;
};

export const useChart2 = (
  chartRef,
  anychart,
  receitaBrutaSQMV,
  receitaBrutaConvencional,
  containerRef,
  tableRef,
) => {
  const chartData = {
    title: 'Receita Bruta',
    header: ['#', 'Recomendação Convencional', 'Recomendação SQM VITAS Brasil'],
    rows: [['Receita Bruta', receitaBrutaConvencional, receitaBrutaSQMV]],
  };
  const returnData = chartData.rows[0].slice(1).reverse();
  const updateInformationTable = useMemo(
    () => updateInformationTableFunc(),
    [],
  );

  const dataSet = anychart.data.set(chartData.rows);

  const SqmVitasData = dataSet.mapAs({
    x: 0,
    value: 1,
  });

  const ConvencionalData = dataSet.mapAs({
    x: 0,
    value: 2,
  });

  // create column chart
  const chart = useMemo(() => anychart.column(), [anychart]);
  useEffect(() => {
    chartRef.current = chart;
  }, [chart, chartRef]);

  // set title chart
  chart.title(chartData.title);

  // turn on chart animation
  chart.animation(true);

  // create first series with mapped data
  const SqmVitas = useInstanceOrUpdateChart(chart, SqmVitasData, 'sqm');
  SqmVitas.name(chartData.header[1]);
  SqmVitas.normal().fill('#f26c31');
  SqmVitas.normal().stroke('#f26c31');

  SqmVitas.hovered().fill('#f26c31', 0.5);
  SqmVitas.hovered().stroke('#f26c31', 4);

  SqmVitas.selected().fill('#f26c31', 0.5);
  SqmVitas.selected().stroke('#f26c31', 4);

  // create second series with mapped data
  const Convencional = useInstanceOrUpdateChart(
    chart,
    ConvencionalData,
    'conv',
  );
  Convencional.name(chartData.header[2]);
  Convencional.normal().fill('#00b050');
  Convencional.normal().stroke('#00b050');

  Convencional.hovered().fill('#00b050', 0.5);
  Convencional.hovered().stroke('#00b050', 4);

  Convencional.selected().fill('#00b050', 0.5);
  Convencional.selected().stroke('#00b050', 4);

  chart
    .yAxis()
    .labels()
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)}`,
    );

  chart
    .labels()
    .enabled(true)
    .position('center-top')
    .anchor('center-bottom')
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart.hovered().labels(false);

  // turn on legend and tune it
  chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);

  // interactivity settings and tooltip position
  chart.interactivity().hoverMode('single');

  chart
    .tooltip()
    .positionMode('point')
    .position('center-top')
    .anchor('center-bottom')
    .offsetX(0)
    .offsetY(5)
    .titleFormat('{%X}')
    .format(
      (e) =>
        `${e.seriesName} : ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  // set container id for the chart
  chart.container('chart2');

  // initiate chart drawing
  chart.draw();
  useEffect(() => {
    chart.listen('chartDraw', () => {
      updateInformationTable(containerRef, tableRef, chart);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, updateInformationTableFunc]);

  return returnData;
};

export const useChart3 = (
  chartRef,
  anychart,
  rSQMV,
  rC,
  containerRef,
  tableRef,
) => {
  const chartData = {
    title:
      '"Receita Líquida" (Receita bruta menos somente o custo nutricional)',
    header: ['#', 'Recomendação Convencional', 'Recomendação SQM VITAS Brasil'],
    rows: [['Receita Líquida', rC, rSQMV]],
  };
  const returnData = chartData.rows[0].slice(1).reverse();
  const updateInformationTable = useMemo(
    () => updateInformationTableFunc(),
    [],
  );

  const dataSet = anychart.data.set(chartData.rows);

  const SqmVitasData = dataSet.mapAs({
    x: 0,
    value: 1,
  });

  const ConvencionalData = dataSet.mapAs({
    x: 0,
    value: 2,
  });

  const chart = useMemo(() => anychart.column(), [anychart]);
  useEffect(() => {
    chartRef.current = chart;
  }, [chart, chartRef]);

  chart.title(chartData.title);

  // turn on chart animation
  chart.animation(true);

  // create first series with mapped data
  const SqmVitas = useInstanceOrUpdateChart(chart, SqmVitasData, 'sqm');
  SqmVitas.name(chartData.header[1]);
  SqmVitas.normal().fill('#f26c31');
  SqmVitas.normal().stroke('#f26c31');

  SqmVitas.hovered().fill('#f26c31', 0.5);
  SqmVitas.hovered().stroke('#f26c31', 4);

  SqmVitas.selected().fill('#f26c31', 0.5);
  SqmVitas.selected().stroke('#f26c31', 4);

  // create second series with mapped data
  const Convencional = useInstanceOrUpdateChart(
    chart,
    ConvencionalData,
    'conv',
  );
  Convencional.name(chartData.header[2]);
  Convencional.normal().fill('#00b050');
  Convencional.normal().stroke('#00b050');

  Convencional.hovered().fill('#00b050', 0.5);
  Convencional.hovered().stroke('#00b050', 4);

  Convencional.selected().fill('#00b050', 0.5);
  Convencional.selected().stroke('#00b050', 4);

  chart
    .yAxis()
    .labels()
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)}`,
    );

  chart
    .labels()
    .enabled(true)
    .position('center-top')
    .anchor('center-bottom')
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart.hovered().labels(false);

  // turn on legend and tune it
  chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);

  // interactivity settings and tooltip position
  chart.interactivity().hoverMode('single');

  chart
    .tooltip()
    .positionMode('point')
    .position('center-top')
    .anchor('center-bottom')
    .offsetX(0)
    .offsetY(5)
    .titleFormat('{%X}')
    .format(
      (e) =>
        `${e.seriesName} : R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)}`,
    );

  // set container id for the chart
  chart.container('chart3');

  // initiate chart drawing
  chart.draw();
  useEffect(() => {
    chart.listen('chartDraw', () => {
      updateInformationTable(containerRef, tableRef, chart);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, updateInformationTableFunc]);

  return returnData;
};

export const useChart4 = (chartRef, anychart, rbSQMV, rlSQMV, rbC, rlC) => {
  const chartData = {
    title: ' ',
    header: ['#', 'Recomendação Convencional', 'Recomendação SQM VITAS Brasil'],
    rows: [
      ['Receita Bruta', rbC, rbSQMV],
      ['Receita Líquida', rlC, rlSQMV],
    ],
  };

  const dataSet = anychart.data.set(chartData.rows);

  const SqmVitasData = dataSet.mapAs({
    x: 0,
    value: 1,
  });

  const ConvencionalData = dataSet.mapAs({
    x: 0,
    value: 2,
  });

  // create column chart
  const chart = useMemo(() => anychart.column(), [anychart]);
  useEffect(() => {
    chartRef.current = chart;
  }, [chart, chartRef]);

  // set title chart
  chart.title(chartData.title);

  // turn on chart animation
  chart.animation(true);

  // create first series with mapped data
  const SqmVitas = useInstanceOrUpdateChart(chart, SqmVitasData, 'sqm');
  SqmVitas.name(chartData.header[1]);
  SqmVitas.normal().fill('#f26c31');
  SqmVitas.normal().stroke('#f26c31');

  SqmVitas.hovered().fill('#f26c31', 0.5);
  SqmVitas.hovered().stroke('#f26c31', 4);

  SqmVitas.selected().fill('#f26c31', 0.5);
  SqmVitas.selected().stroke('#f26c31', 4);

  // create second series with mapped data
  const Convencional = useInstanceOrUpdateChart(
    chart,
    ConvencionalData,
    'conv',
  );
  Convencional.name(chartData.header[2]);
  Convencional.normal().fill('#00b050');
  Convencional.normal().stroke('#00b050');

  Convencional.hovered().fill('#00b050', 0.5);
  Convencional.hovered().stroke('#00b050', 4);

  Convencional.selected().fill('#00b050', 0.5);
  Convencional.selected().stroke('#00b050', 4);

  chart
    .yAxis()
    .labels()
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart
    .labels()
    .enabled(true)
    .position('center-top')
    .anchor('center-bottom')
    .format(
      (e) =>
        `R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)} /ha`,
    );

  chart.hovered().labels(false);

  // turn on legend and tune it
  chart.legend().enabled(true).fontSize(13).padding([0, 0, 20, 0]);

  // interactivity settings and tooltip position
  chart.interactivity().hoverMode('single');

  chart
    .tooltip()
    .positionMode('point')
    .position('center-top')
    .anchor('center-bottom')
    .offsetX(0)
    .offsetY(5)
    .titleFormat('{%X}')
    .format(
      (e) =>
        `${e.seriesName}: R$ ${Intl.NumberFormat('pt-br', {
          maximumFractionDigits: 2,
          minimumFractionDigits: 2,
        }).format(e.value)}`,
    );

  // set container id for the chart
  chart.container('chart4');

  // initiate chart drawing
  chart.draw();
};

export default {
  renderChart1: useChart1,
  renderChart2: useChart2,
  renderChart3: useChart3,
  renderChart4: useChart4,
};
